import type { Components, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { radius } from '../palette';
import { touch } from './base';

type Tone = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export const data: Components<Theme> = {
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme: t }) => ({ paddingTop: 10, paddingBottom: 10, borderColor: t.palette.divider }),
      head: ({ theme: t }) => ({ fontWeight: 500, color: t.palette.text.secondary, fontSize: '0.75rem', whiteSpace: 'nowrap', backgroundColor: t.palette.background.paper }),
    },
  },
  MuiTableRow: { styleOverrides: { root: ({ theme: t }) => ({ '&.MuiTableRow-hover:hover': { backgroundColor: alpha(t.palette.background.default, 0.7) }, '&:last-child td': { borderBottom: 0 } }) } },
  MuiTableSortLabel: { styleOverrides: { root: ({ theme: t }) => ({ '&.Mui-active': { color: t.palette.text.primary } }) } },
  // On a phone the rows-per-page picker is the part that pushes the pager off the card, so it goes.
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme: t }) => ({ borderTop: `1px solid ${t.palette.divider}`, color: t.palette.text.secondary }),
      displayedRows: { fontSize: '0.75rem' },
      toolbar: { flexWrap: 'wrap', '@media (max-width: 599.95px)': { paddingLeft: 8, paddingRight: 4 } },
      selectLabel: { fontSize: '0.75rem', '@media (max-width: 599.95px)': { display: 'none' } },
      input: { '@media (max-width: 599.95px)': { display: 'none' } },
      actions: { '@media (max-width: 599.95px)': { marginLeft: 8 } },
    },
  },
  // Status marks are pale tints with dark text, squared off so they never read as buttons.
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: radius.mark, fontWeight: 500, letterSpacing: '-0.005em' },
      sizeSmall: { height: 22, fontSize: '0.75rem', '& .MuiChip-label': { paddingInline: 7 } },
      filled: ({ ownerState, theme: t }) => {
        if (!ownerState.color || ownerState.color === 'default') return { backgroundColor: t.palette.action.selected, color: t.palette.text.primary };
        const c = t.palette[ownerState.color as Tone];
        return { backgroundColor: alpha(c.main, 0.11), color: c.dark, '& .MuiChip-icon': { color: c.main } };
      },
      outlined: ({ ownerState, theme: t }) => {
        if (!ownerState.color || ownerState.color === 'default') return { borderColor: t.palette.divider, backgroundColor: t.palette.background.paper };
        const c = t.palette[ownerState.color as Tone];
        return { borderColor: alpha(c.main, 0.35), color: c.dark };
      },
      clickable: ({ theme: t }) => ({ '&:hover': { backgroundColor: t.palette.action.selected }, [touch]: { minHeight: 32 } }),
      deleteIcon: { fontSize: 16 },
    },
  },
  MuiAvatar: { defaultProps: { variant: 'rounded' }, styleOverrides: { rounded: { borderRadius: '30%' } } },
  MuiBadge: { styleOverrides: { badge: { fontWeight: 600, fontSize: '0.6875rem', minWidth: 18, height: 18 } } },
  MuiLinearProgress: { styleOverrides: { root: ({ theme: t }) => ({ borderRadius: 2, height: 6, backgroundColor: t.palette.action.selected }), bar: { borderRadius: 2 } } },
  MuiCircularProgress: { defaultProps: { thickness: 4.5 } },
  MuiSkeleton: {
    defaultProps: { animation: 'wave' },
    styleOverrides: { root: ({ theme: t }) => ({ backgroundColor: t.palette.action.selected }), rounded: { borderRadius: radius.control } },
  },
  MuiList: { styleOverrides: { root: { paddingBlock: 4 } } },
  MuiListItemButton: { styleOverrides: { root: ({ theme: t }) => ({ borderRadius: radius.control, '&.Mui-selected': { backgroundColor: t.palette.action.selected } }) } },
  MuiListItemIcon: { styleOverrides: { root: { minWidth: 36, color: 'inherit' } } },
  MuiStepIcon: { styleOverrides: { root: ({ theme: t }) => ({ color: t.palette.action.selected, '&.Mui-active, &.Mui-completed': { color: t.palette.primary.main } }), text: { fontWeight: 600 } } },
  MuiStepLabel: { styleOverrides: { label: { fontSize: '0.8125rem', '&.Mui-active, &.Mui-completed': { fontWeight: 500 } } } },
};
